import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { getJobById, applyForJob } from '../../services/firestoreService';
import { useAuth } from '../../contexts/AuthContext';
import ApplicationModal from '../../components/applications/ApplicationModal';
import { SEED_JOBS } from '../../data/seedJobs';
import ApplicationsReceived from '../../components/applications/ApplicationsReceived';
import { submitReferral } from '../../services/applicationService';

const JobDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const { currentUser, isAdmin } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [showReferModal, setShowReferModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Check if the URL has the apply=true parameter
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('apply') === 'true') {
      setShowApplyModal(true);
    }
  }, [location]);

  // Form states
  const [application, setApplication] = useState({
    name: '',
    email: '',
    description: '',
    attachment: null
  });

  const [referral, setReferral] = useState({
    name: '',
    email: '',
    relationship: '',
    note: ''
  });

  // Load job from Firebase
  const loadJob = useCallback(async () => {
    try {
      setLoading(true);
      if (typeof id === 'string' && id.startsWith('seed-job-')) {
        setJob(SEED_JOBS.find((j) => j.id === id) || null);
        setLoading(false);
        return;
      }
      const jobData = await getJobById(id);
      setJob(jobData);
    } catch (err) {
      console.error('Error loading job:', err);
      setJob(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadJob();
  }, [loadJob]);

  // Pre-fill name/email from current user
  useEffect(() => {
    if (currentUser) {
      setApplication(prev => ({
        ...prev,
        name: currentUser.displayName || '',
        email: currentUser.email || ''
      }));
    }
  }, [currentUser]);
  const handleApplyInputChange = (e) => {
    const { name, value } = e.target;
    setApplication({
      ...application,
      [name]: value
    });
  };

  const handleFileChange = (e) => {
    setApplication({
      ...application,
      attachment: e.target.files[0] || null
    });
  };

  const handleReferInputChange = (e) => {
    const { name, value } = e.target;
    setReferral({
      ...referral,
      [name]: value
    });
  };

  const handleApplySubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      await applyForJob(id, currentUser?.uid, {
        name: application.name,
        email: application.email,
        description: application.description,
        hasAttachment: !!application.attachment,
        attachmentName: application.attachment?.name || null
      });
      alert('Your application has been submitted successfully!');
      setShowApplyModal(false);
      setApplication({ name: currentUser?.displayName || '', email: currentUser?.email || '', description: '', attachment: null });
      loadJob();
    } catch (err) {
      console.error('Error submitting application:', err);
      alert('Failed to submit application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReferSubmit = async (e) => {
    e.preventDefault();
    try {
      await submitReferral(job, 'job', currentUser?.uid, referral);
      alert('Your referral has been submitted successfully!');
      setShowReferModal(false);
      setReferral({ name: '', email: '', relationship: '', note: '' });
    } catch (err) {
      console.error('Error submitting referral:', err);
      alert('Failed to submit referral. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-apn-blue"></div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="bg-white min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900">Job Not Found</h1>
            <p className="mt-4 text-lg text-gray-600">The job you're looking for doesn't exist or has been removed.</p>
            <Link to="/jobs" className="mt-6 inline-block bg-apn-blue text-white py-2 px-6 rounded hover:bg-blue-700 transition duration-200">
              Back to Job Board
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Page Header */}
      <div className="bg-apn-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* APN Logo */}
            <div className="flex justify-center mb-4">
              <img
                src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-white.jpg`}
                alt="APN Logo"
                className="h-16"
              />
            </div>
            <h1 className="text-3xl font-bold">{job.title}</h1>
            <p className="mt-2 text-lg">
              {job.company}
              {job.location && ` • ${job.location}`}
            </p>
          </div>
        </div>
      </div>

      {/* Job Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            {/* Job Header */}
            <div className="bg-gray-50 p-6 border-b border-gray-200">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
                <div>
                  <div className="flex items-center">
                    <h2 className="text-2xl font-bold text-gray-900">{job.title}</h2>
                    {job.featured && (
                      <span className="ml-2 inline-block bg-apn-blue text-white text-xs px-2 py-1 rounded">
                        Featured
                      </span>
                    )}
                  </div>
                  <p className="text-gray-600 mt-1">{job.company}{job.location && ` • ${job.location}`}</p>
                </div>
                <div className="mt-4 md:mt-0 flex space-x-4">
                  <button
                    onClick={() => setShowApplyModal(true)}
                    className="inline-flex items-center bg-apn-blue text-white py-2 px-6 rounded hover:bg-blue-700 transition duration-200"
                  >
                    Apply Now
                  </button>
                  <button
                    onClick={() => setShowReferModal(true)}
                    className="inline-flex items-center bg-white text-apn-blue border border-apn-blue py-2 px-6 rounded hover:bg-gray-50 transition duration-200"
                  >
                    Refer Someone
                  </button>
                </div>
              </div>
            </div>

            {/* Job Details */}
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                {(job.type || job.jobType) && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-2">Job Type</h3>
                    <p className="text-gray-700">{job.type || job.jobType}</p>
                  </div>
                )}
                {job.location && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-2">Location</h3>
                    <p className="text-gray-700">{job.location}</p>
                  </div>
                )}
                {job.salary && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-900 mb-2">Salary</h3>
                    <p className="text-gray-700">{job.salary}</p>
                  </div>
                )}
              </div>

              {(job.description || job.detailedDescription) && (
                <div className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Job Description</h3>
                  {job.description && <p className="text-gray-700 mb-4">{job.description}</p>}
                  {job.detailedDescription && <p className="text-gray-700">{job.detailedDescription}</p>}
                </div>
              )}

              {job.responsibilities && job.responsibilities.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Responsibilities</h3>
                  <ul className="list-disc pl-5 text-gray-700 space-y-2">
                    {job.responsibilities.map((responsibility, index) => (
                      <li key={index}>{responsibility}</li>
                    ))}
                  </ul>
                </div>
              )}

              {Array.isArray(job.requirements) && job.requirements.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Requirements</h3>
                  <ul className="list-disc pl-5 text-gray-700 space-y-2">
                    {job.requirements.map((requirement, index) => (
                      <li key={index}>{requirement}</li>
                    ))}
                  </ul>
                </div>
              )}

              {job.benefits && job.benefits.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Benefits</h3>
                  <ul className="list-disc pl-5 text-gray-700 space-y-2">
                    {job.benefits.map((benefit, index) => (
                      <li key={index}>{benefit}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="border-t border-gray-200 pt-6">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
                  <div>
                    {job.postedDate && (
                      <p className="text-gray-600">Posted on {job.postedDate?.toDate ? job.postedDate.toDate().toLocaleDateString() : new Date(job.postedDate).toLocaleDateString()}</p>
                    )}
                    {job.applicationDeadline && (
                      <p className="text-gray-600">Application Deadline: {new Date(job.applicationDeadline).toLocaleDateString()}</p>
                    )}
                  </div>
                  <div className="mt-4 md:mt-0 flex space-x-4">
                    <button
                      onClick={() => setShowApplyModal(true)}
                      className="inline-flex items-center bg-apn-blue text-white py-2 px-6 rounded hover:bg-blue-700 transition duration-200"
                    >
                      Apply Now
                    </button>
                    <button
                      onClick={() => setShowReferModal(true)}
                      className="inline-flex items-center bg-white text-apn-blue border border-apn-blue py-2 px-6 rounded hover:bg-gray-50 transition duration-200"
                    >
                      Refer Someone
                    </button>
                  </div>
                </div>
              </div>

              {/* Creator/admin: review applications + referrals received for this job. */}
              {job && currentUser && (currentUser.uid === job.postedBy || isAdmin()) && (
                <div className="mt-8">
                  <ApplicationsReceived postingId={id} creatorId={job.postedBy} />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Apply — unified through the shared ApplicationModal → submitApplication
          (creates a real `applications` doc + notifies the poster). */}
      {showApplyModal && job && (
        <ApplicationModal
          isOpen={showApplyModal}
          onClose={() => setShowApplyModal(false)}
          posting={job}
          type="job"
        />
      )}

      {/* Legacy inline apply modal — disabled, superseded by ApplicationModal above. */}
      {false && showApplyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-900">Apply for Job</h2>
                <button
                  onClick={() => setShowApplyModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-gray-600 mb-6">
                You are applying for: <span className="font-semibold">{job.title}</span> at <span className="font-semibold">{job.company}</span>
              </p>
              <form onSubmit={handleApplySubmit}>
                <div className="mb-4">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="applyName"
                    name="name"
                    value={application.name}
                    onChange={handleApplyInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                    placeholder="Your full name"
                  />
                </div>
                <div className="mb-4">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="applyEmail"
                    name="email"
                    value={application.email}
                    onChange={handleApplyInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                    placeholder="Your email address"
                  />
                </div>
                <div className="mb-4">
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    rows="5"
                    value={application.description}
                    onChange={handleApplyInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                    placeholder="Introduce yourself and explain why you're a good fit for this position..."
                  ></textarea>
                </div>
                <div className="mb-6">
                  <label htmlFor="attachment" className="block text-sm font-medium text-gray-700 mb-1">
                    Attachment (optional)
                  </label>
                  <input
                    type="file"
                    id="attachment"
                    name="attachment"
                    accept=".pdf,.doc,.docx,.txt"
                    onChange={handleFileChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                  />
                  <p className="text-xs text-gray-500 mt-1">Accepted formats: PDF, DOC, DOCX, TXT</p>
                </div>
                <div className="flex justify-end space-x-4">
                  <button
                    type="button"
                    onClick={() => setShowApplyModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-4 py-2 bg-apn-blue text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {submitting ? 'Submitting...' : 'Submit Application'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Refer Modal */}
      {showReferModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-900">Refer Someone for this Job</h2>
                <button
                  onClick={() => setShowReferModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-gray-600 mb-6">
                You are referring someone for: <span className="font-semibold">{job.title}</span> at <span className="font-semibold">{job.company}</span>
              </p>
              <form onSubmit={handleReferSubmit}>
                <div className="mb-4">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                    Referral's Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={referral.name}
                    onChange={handleReferInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                    placeholder="Full name of the person you're referring"
                  />
                </div>
                <div className="mb-4">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Referral's Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={referral.email}
                    onChange={handleReferInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                    placeholder="Email address of the person you're referring"
                  />
                </div>
                <div className="mb-4">
                  <label htmlFor="relationship" className="block text-sm font-medium text-gray-700 mb-1">
                    Your Relationship
                  </label>
                  <select
                    id="relationship"
                    name="relationship"
                    value={referral.relationship}
                    onChange={handleReferInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                  >
                    <option value="">Select your relationship</option>
                    <option value="Colleague">Colleague</option>
                    <option value="Classmate">Classmate</option>
                    <option value="Friend">Friend</option>
                    <option value="Professional Connection">Professional Connection</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="mb-6">
                  <label htmlFor="note" className="block text-sm font-medium text-gray-700 mb-1">
                    Referral Note
                  </label>
                  <textarea
                    id="note"
                    name="note"
                    rows="4"
                    value={referral.note}
                    onChange={handleReferInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apn-blue"
                    placeholder="Why do you think this person would be a good fit for the job?"
                  ></textarea>
                </div>
                <div className="flex justify-end space-x-4">
                  <button
                    type="button"
                    onClick={() => setShowReferModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-apn-blue text-white rounded-md hover:bg-blue-700"
                  >
                    Submit Referral
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Call to Action */}
      <section className="bg-apn-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* APN Logo */}
          <div className="flex justify-center mb-6">
            <img
              src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-SQ-white.jpg`}
              alt="APN Logo Square"
              className="h-20"
            />
          </div>
          <h2 className="text-3xl font-bold mb-4">Find More Jobs</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Browse our job board to find more opportunities that match your skills and career goals.
          </p>
          <Link to="/jobs" className="btn-primary bg-white text-apn-blue hover:bg-gray-100">
            View All Jobs
          </Link>
        </div>
      </section>
    </div>
  );
};

export default JobDetail;
